import React from "react";
import { getYoutubeVideos } from "../../services/api/http";
import SiteLayout from "../../components/layout/SiteLayout";
import PageIntro from "../../components/ui/PageIntro";
import { FaPause, FaPlay, FaTimes, FaYoutube } from "react-icons/fa";
import "./ReligiousVideosPage.css";

const buildYouTubeVideoId = (webContentLink) => {
  try {
    const url = new URL(webContentLink);
    if (url.searchParams.get("v")) {
      return url.searchParams.get("v");
    }

    const host = url.hostname.replace(/^www\./, "");
    const pathParts = url.pathname.split("/").filter(Boolean);

    if (host === "youtu.be") {
      return pathParts[0] || null;
    }

    const embedIndex = pathParts.indexOf("embed");
    if (embedIndex >= 0 && pathParts[embedIndex + 1]) {
      return pathParts[embedIndex + 1];
    }

    return pathParts[pathParts.length - 1] || null;
  } catch (error) {
    return null;
  }
};

const buildThumbnailUrl = (webContentLink) => {
  const videoId = buildYouTubeVideoId(webContentLink);
  if (!videoId) {
    return null;
  }

  return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
};

let youtubeApiPromise = null;

const loadYouTubeIframeApi = () => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("YouTube API is not available on the server."));
  }

  if (window.YT && window.YT.Player) {
    return Promise.resolve(window.YT);
  }

  if (!youtubeApiPromise) {
    youtubeApiPromise = new Promise((resolve, reject) => {
      const finish = () => {
        if (window.YT && window.YT.Player) {
          resolve(window.YT);
          return;
        }

        reject(new Error("YouTube IFrame API did not initialize."));
      };

      const previousReady = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof previousReady === "function") {
          previousReady();
        }
        finish();
      };

      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const script = document.createElement("script");
        script.src = "https://www.youtube.com/iframe_api";
        script.async = true;
        script.onerror = () => {
          youtubeApiPromise = null;
          reject(new Error("Failed to load the YouTube IFrame API."));
        };
        document.head.appendChild(script);
      }

      window.setTimeout(() => {
        if (window.YT && window.YT.Player) {
          finish();
        }
      }, 0);
    });
  }

  return youtubeApiPromise;
};

class LazyVideoEmbed extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      shouldRender: false
    };
    this.containerRef = React.createRef();
    this.observer = null;
  }

  componentDidMount() {
    if (typeof IntersectionObserver !== "function") {
      this.setState({ shouldRender: true });
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.setState({ shouldRender: true });
            if (this.observer) {
              this.observer.disconnect();
              this.observer = null;
            }
          }
        });
      },
      {
        rootMargin: "300px 0px",
        threshold: 0.01
      }
    );

    if (this.containerRef.current) {
      this.observer.observe(this.containerRef.current);
    }
  }

  componentWillUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  render() {
    const { title, webContentLink } = this.props;
    const { shouldRender } = this.state;

    return (
      <div className="responsive-video" ref={this.containerRef}>
        {shouldRender ? (
          <ThemedVideoFrame title={title} webContentLink={webContentLink} />
        ) : (
          <div className="video-placeholder">
            <span>Loading player on scroll</span>
          </div>
        )}
      </div>
    );
  }
}

class ThemedVideoFrame extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isPlaying: false,
      isReady: false,
      currentTime: 0,
      duration: 0,
      playerState: "idle",
      playerError: "",
    };
    this.playerMountRef = React.createRef();
    this.player = null;
    this.progressTimer = null;
  }

  componentDidMount() {
    loadYouTubeIframeApi().catch(() => {
      // Defer error handling until a play attempt so the preview still renders.
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.isPlaying && !prevState.isPlaying) {
      this.initializePlayer();
    }

    if (!this.state.isPlaying && prevState.isPlaying) {
      this.destroyPlayer();
    }
  }

  componentWillUnmount() {
    this.destroyPlayer();
  }

  startPlayback = () => {
    this.setState({
      isPlaying: true,
      isReady: false,
      currentTime: 0,
      duration: 0,
      playerState: "loading",
      playerError: "",
    });
  };

  stopPlayback = () => {
    this.setState({
      isPlaying: false,
      isReady: false,
      currentTime: 0,
      duration: 0,
      playerState: "idle",
      playerError: "",
    });
  };

  initializePlayer = async () => {
    if (this.player || !this.playerMountRef.current) {
      return;
    }

    try {
      await loadYouTubeIframeApi();
    } catch (error) {
      this.setState({
        playerError: "Unable to load the video player.",
        playerState: "error",
      });
      return;
    }

    const videoId = buildYouTubeVideoId(this.props.webContentLink);
    if (!videoId || !this.playerMountRef.current) {
      this.setState({
        playerError: "Unable to load this video.",
        playerState: "error",
      });
      return;
    }

    this.player = new window.YT.Player(this.playerMountRef.current, {
      videoId,
      playerVars: {
        autoplay: 1,
        controls: 0,
        disablekb: 1,
        fs: 0,
        iv_load_policy: 3,
        modestbranding: 1,
        origin: window.location.origin,
        playsinline: 1,
        rel: 0,
      },
      events: {
        onReady: this.handlePlayerReady,
        onStateChange: this.handlePlayerStateChange,
        onError: this.handlePlayerError,
      },
    });
  };

  destroyPlayer = () => {
    this.stopProgressPolling();

    if (this.player) {
      try {
        this.player.destroy();
      } catch (error) {
        // Ignore teardown errors on fast route changes.
      }
    }

    this.player = null;
  };

  startProgressPolling = () => {
    this.stopProgressPolling();
    this.progressTimer = window.setInterval(this.syncProgress, 500);
  };

  stopProgressPolling = () => {
    if (this.progressTimer) {
      window.clearInterval(this.progressTimer);
      this.progressTimer = null;
    }
  };

  syncProgress = () => {
    if (!this.player || !this.state.isPlaying || !this.state.isReady) {
      return;
    }

    const currentTime = this.player.getCurrentTime?.() || 0;
    const duration = this.player.getDuration?.() || 0;

    this.setState({
      currentTime,
      duration,
    });
  };

  handlePlayerReady = (event) => {
    const duration = event.target.getDuration?.() || 0;

    this.setState(
      {
        isReady: true,
        duration,
        currentTime: 0,
        playerState: "playing",
      },
      () => {
        this.startProgressPolling();
        event.target.playVideo();
      }
    );
  };

  handlePlayerStateChange = (event) => {
    const { PlayerState } = window.YT || {};

    switch (event.data) {
      case PlayerState?.PLAYING:
        this.setState({ isReady: true, playerState: "playing" });
        this.startProgressPolling();
        break;
      case PlayerState?.PAUSED:
        this.setState({ playerState: "paused" });
        this.stopProgressPolling();
        this.syncProgress();
        break;
      case PlayerState?.ENDED:
        this.setState({ playerState: "ended" });
        this.stopProgressPolling();
        this.syncProgress();
        break;
      case PlayerState?.BUFFERING:
        this.setState({ playerState: "buffering", isReady: true });
        break;
      default:
        break;
    }
  };

  handlePlayerError = () => {
    this.setState({
      playerError: "The video player could not start. Open the video on YouTube.",
      playerState: "error",
    });
    this.stopProgressPolling();
  };

  togglePlayback = () => {
    if (!this.player) {
      return;
    }

    const state = this.player.getPlayerState?.();
    if (state === window.YT.PlayerState.PLAYING) {
      this.player.pauseVideo();
      return;
    }

    if (state === window.YT.PlayerState.ENDED) {
      this.player.seekTo(0, true);
    }

    this.player.playVideo();
  };

  handleSeek = (event) => {
    if (!this.player) {
      return;
    }

    const nextTime = Number(event.target.value);
    this.player.seekTo(nextTime, true);
    this.setState({
      currentTime: nextTime,
      playerState: "playing",
    });
  };

  formatTime = (seconds) => {
    const totalSeconds = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;
    return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
  };

  render() {
    const { title, webContentLink } = this.props;
    const {
      isPlaying,
      isReady,
      currentTime,
      duration,
      playerState,
      playerError,
    } = this.state;
    const thumbnailUrl = buildThumbnailUrl(webContentLink);
    const progressPercent =
      duration > 0 ? Math.max(0, Math.min(100, (currentTime / duration) * 100)) : 0;
    const sliderBackground = `linear-gradient(90deg, rgba(57, 199, 255, 0.9) 0%, rgba(57, 199, 255, 0.9) ${progressPercent}%, rgba(255, 255, 255, 0.12) ${progressPercent}%, rgba(255, 255, 255, 0.12) 100%)`;
    const isActive = playerState === "playing" || playerState === "buffering";

    return (
      <div className={`video-player-shell ${isPlaying ? "is-playing" : ""}`}>
        {isPlaying ? (
          <>
            <div ref={this.playerMountRef} className="video-player-embed" />
            <div className="video-player-controls">
              <div className="video-player-controls-row">
                <button
                  type="button"
                  className={`video-player-control-button ${isActive ? "is-primary" : ""}`}
                  onClick={this.togglePlayback}
                  aria-label={isActive ? `Pause ${title}` : `Play ${title}`}
                >
                  {isActive ? <FaPause /> : <FaPlay />}
                </button>
                <div className="video-player-control-copy">
                  <div className="video-player-control-title">{title}</div>
                  {isReady ? null : (
                    <div className="video-player-control-subtitle">Loading player</div>
                  )}
                </div>
                <div className="video-player-timecode">
                  {this.formatTime(currentTime)} / {this.formatTime(duration)}
                </div>
                <button
                  type="button"
                  className="video-player-close"
                  onClick={this.stopPlayback}
                  aria-label={`Close ${title} video`}
                >
                  <FaTimes />
                </button>
              </div>
              <input
                className="video-player-timeline"
                type="range"
                min="0"
                max={duration > 0 ? duration : 0}
                step="0.1"
                value={duration > 0 ? Math.min(currentTime, duration) : 0}
                onChange={this.handleSeek}
                disabled={!isReady || duration <= 0}
                style={{ background: sliderBackground }}
                aria-label={`${title} playback progress`}
              />
              {playerError ? <div className="video-player-error">{playerError}</div> : null}
            </div>
          </>
        ) : (
          <button
            type="button"
            className="video-player-preview"
            onClick={this.startPlayback}
          >
            {thumbnailUrl ? (
              <img
                className="video-player-poster"
                src={thumbnailUrl}
                alt=""
                aria-hidden="true"
              />
            ) : null}
            <span className="video-player-overlay" aria-hidden="true" />
            <div className="video-player-preview-top">
              <span className="video-player-badge">
                <FaYoutube /> Vishwaram Maharaj
              </span>
              <span className="video-player-caption">Tap to play</span>
            </div>
            <div className="video-player-center">
              <span className="video-player-button">
                <FaPlay />
              </span>
              <div>
                <div className="video-player-title">{title}</div>
              </div>
            </div>
          </button>
        )}
      </div>
    );
  }
}

export default class ReligiousVideosPage extends React.Component {
  constructor() {
    super();
    this.state = {
      videos: [],
    };
  }

  componentDidMount() {
    getYoutubeVideos()
      .then((items) => {
        const videos = [];
        for (const item of items.data) {
          videos.push({
            webContentLink: item.webContentLink,
            title: item.name,
            description: item.description
          });
        }

        this.setState({ videos });
      })
      .catch((error) => {
        console.error(error);
      });
  }

  render() {
    const { videos } = this.state;

    return (
      <SiteLayout>
        <main className="page-shell home-v3-shell">
          <PageIntro
            eyebrow="Spirituality / Videos"
            title="Teachings and rituals, in one library."
            description="Ceremonies, talks, and recorded religious media collected in one place for quick access."
          />

          <section className="section">
            <div className="home-v3-band-label">
              <span className="home-v3-kicker">Library</span>
              <h2>What I share here.</h2>
              <p>
                I collect recorded talks and ceremony media from the video
                library here.
              </p>
            </div>
            <div className="feature-grid video-library-grid">
              {videos.map((video) => (
                <div className="video-card" key={video.title}>
                  <div className="video-card-body">
                    {video.description ? (
                      <p className="feature-copy">{video.description}</p>
                    ) : null}
                  </div>
                  <LazyVideoEmbed
                    title={video.title}
                    webContentLink={video.webContentLink}
                  />
                </div>
              ))}
            </div>
          </section>
        </main>
      </SiteLayout>
    );
  }
}
